<template>
    <div :id="`location-${location.id}`" class="location-view">
        <h2>
            <span class="icon material-symbols-outlined">location_on</span>
            {{ location.name }}
        </h2>
        <div v-if="getLocationImage !== null" class="image-wrapper">
            <ExternalImage :src="getLocationImage" />
        </div>
        <div class="trip-data">
            <div class="user-data-item date">
                <span class="icon material-symbols-outlined">
                    {{ roleIcon }}
                </span>
                <div>
                    <span class="title">{{ roleTitle }}</span>
                    <div class="user-input">
                        {{ roleDate }}
                    </div>
                </div>
            </div>
            <div v-if="location.expenses" class="user-data-item expenses-total">
                <span class="icon material-symbols-outlined">payments</span>
                <div>
                    <span class="title">Total expenses</span>
                    <div class="user-input">
                        ${{ $formatMoney(location.expenses) }}
                    </div>
                </div>
            </div>
            <div
                v-if="location.share < 1"
                class="user-data-item expenses-share"
            >
                <span class="icon material-symbols-outlined">payments</span>
                <div>
                    <span class="title">Your expenses</span>
                    <div class="user-input">
                        ${{ $formatMoney(getUserCost) }}
                    </div>
                </div>
            </div>
            <div v-if="transportMot" class="user-data-item transport">
                <span class="icon material-symbols-outlined">
                    {{ $iconGet("transport", transportMot) }}
                </span>
                <div>
                    <span class="title">{{ transportTitle }}</span>
                    <div class="user-input">
                        {{ $enumGet("transport", transportMot) }}
                    </div>
                </div>
            </div>
            <div v-if="location.host" class="user-data-item">
                <span class="icon material-symbols-outlined">person</span>
                <div>
                    <span class="title">Host</span>
                    <div class="user-input">
                        {{ location.host }}
                    </div>
                </div>
            </div>
            <div v-if="location.impression !== null" class="user-data-item">
                <span class="icon material-symbols-outlined">
                    {{ $iconGet("impression", location.impression) }}
                </span>
                <div>
                    <span class="title">Impression</span>
                    <div class="user-input">
                        {{ $enumGet("impression", location.impression) }}
                    </div>
                </div>
            </div>
            <div v-if="localName" class="user-data-item">
                <span class="icon material-symbols-outlined">translate</span>
                <div>
                    <span class="title">Local Name</span>
                    <div class="user-input">
                        {{ localName }}
                    </div>
                </div>
            </div>
        </div>
        <div class="trip-data">
            <div v-if="location.notes" class="user-data-item full-width notes">
                <span class="icon material-symbols-outlined">text_snippet</span>
                <div>
                    <span class="title">Your notes</span>
                    <div class="user-input notes">{{ location.notes }}</div>
                </div>
            </div>
        </div>
        <div class="actions">
            <router-link
                class="edit-location"
                :to="`/trips/${tripId}/locations/${location.id}/edit`"
            >
                <Bttn>Edit location</Bttn>
            </router-link>
        </div>
    </div>
</template>

<script>
import ExternalImage from "js/components/common/ExternalImage.vue";

function normalize(value) {
    return (value || "")
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .toLowerCase()
        .trim();
}

export default {
    name: "LocationView",
    components: {
        ExternalImage,
    },
    props: {
        location: {
            type: Object,
            required: true,
        },
        tripId: {
            type: Number,
            required: true,
        },
        role: {
            type: Object,
            default: null,
        },
        impliedMot: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            loading: true,
        };
    },
    computed: {
        getUserCost() {
            return Math.round(this.location.share * this.location.expenses);
        },
        getLocationImage() {
            return this.location.image_url ?? null;
        },
        transportMot() {
            return this.location.mot || this.impliedMot;
        },
        transportTitle() {
            return this.location.mot ? "Transport" : "Transport (from trip)";
        },
        roleIcon() {
            if (this.role?.type === "stay") {
                return "nights_stay";
            }

            return "calendar_month";
        },
        roleTitle() {
            if (this.role?.type === "stay") {
                return "Stay";
            }
            if (this.role?.type === "day-trip") {
                return `Day trip from ${this.role.stayName}`;
            }
            if (this.role?.type === "transit" && this.role.onTheWay) {
                return "On the way";
            }
            if (this.role?.type === "visit") {
                return "Visit";
            }

            return "Date";
        },
        roleDate() {
            if (this.role?.type === "stay") {
                return `${this.$formatDate(this.role.start)} – ${this.$formatDate(
                    this.role.end
                )} · ${this.role.nights} ${
                    this.role.nights === 1 ? "night" : "nights"
                }`;
            }

            return this.$formatDate(this.role?.date ?? this.location.date);
        },
        localName() {
            const city = this.location.geo?.city;
            const name = this.location.geo?.name;
            if (!city || !name) {
                return null;
            }

            return normalize(city) === normalize(name) ? null : city;
        },
    },
    methods: {},
};
</script>
